import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Layers3, Library, User } from 'lucide-react';
import { ICON_SIZE } from '../../design-system';
import { ui$ } from '../../store';
import './MobileTabBar.css';

/**
 * The four destinations the desktop sidebar actually holds; everything below
 * them there is content, and moves into the Library switcher or Me.
 *
 * `owns` decides the active tab for routes the tab's own `to` does not cover —
 * a named list still belongs to Library, a deck still belongs to Decks.
 */
const TABS = [
  {
    key: 'today',
    icon: <Home size={ICON_SIZE.lg} aria-hidden="true" />,
    labelKey: 'sidebar:dashboard',
    to: () => '/dashboard',
    owns: (path) => path === '/dashboard',
  },
  {
    key: 'library',
    icon: <Library size={ICON_SIZE.lg} aria-hidden="true" />,
    labelKey: 'sidebar:library',
    to: () => {
      // Returning to Library lands on the list you were last reading in,
      // not a fixed root — currentListId is null for Inbox.
      const listId = ui$.currentListId.get();
      if (listId === 'all') return '/all';
      return listId ? `/list/${listId}` : '/inbox';
    },
    owns: (path) => path === '/all' || path === '/inbox' || path.startsWith('/list/'),
  },
  {
    key: 'decks',
    icon: <Layers3 size={ICON_SIZE.lg} aria-hidden="true" />,
    labelKey: 'sidebar:decks',
    to: () => '/decks',
    owns: (path) => path === '/decks' || path.startsWith('/deck/'),
  },
  {
    key: 'me',
    icon: <User size={ICON_SIZE.lg} aria-hidden="true" />,
    labelKey: 'sidebar:me',
    to: () => '/me',
    owns: (path) => path === '/me' || path.startsWith('/me/') || path.startsWith('/settings'),
  },
];

const MobileTabBar = () => {
  const { t } = useTranslation(['sidebar']);
  const { pathname } = useLocation();

  return (
    <nav className="mobile-tab-bar" aria-label={t('sidebar:navigation')}>
      {TABS.map(({ key, icon, labelKey, to, owns }) => {
        const isActive = owns(pathname);
        return (
          <NavLink
            key={key}
            to={to()}
            className="mobile-tab"
            aria-label={t(labelKey)}
            aria-current={isActive ? 'page' : undefined}
            data-active={isActive || undefined}
            data-testid={`mobile-tab-${key}`}
          >
            {icon}
          </NavLink>
        );
      })}
    </nav>
  );
};

export default MobileTabBar;
